/**
 * HotStreaksStrip - Premium "Players to Watch" cards with hit/miss strips,
 * season averages, threshold bars, and continuation signals.
 *
 * Designed to match the visual density and quality of the GamePerformanceStripCards
 * at the top of the page. Each player gets a mini card.
 *
 * @param {Array} hotPlayers - Hot player objects from game summary
 * @param {Object} homeTeam - {abbreviation}
 * @param {Object} awayTeam - {abbreviation}
 */
import Card from '../../common/Card';

const badgeConfig = {
  blazing: { color: 'bg-orange-400', ring: 'ring-orange-400/30', label: 'On Fire' },
  hot: { color: 'bg-red-400', ring: 'ring-red-400/30', label: 'Hot' },
  warm: { color: 'bg-amber-400', ring: 'ring-amber-400/30', label: 'Trending' },
};

const SignalArrow = ({ signal, size = 'md' }) => {
  const cls = size === 'lg' ? 'w-4 h-4' : 'w-3 h-3';
  if (signal === 'LIKELY_EXTEND') {
    return (
      <svg className={`${cls} text-green-400`} viewBox="0 0 12 12" fill="none">
        <path d="M6 2L10 8H2L6 2Z" fill="currentColor" />
      </svg>
    );
  }
  if (signal === 'LIKELY_REGRESS') {
    return (
      <svg className={`${cls} text-red-400`} viewBox="0 0 12 12" fill="none">
        <path d="M6 10L2 4H10L6 10Z" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg className={`${cls} text-gray-500`} viewBox="0 0 12 12" fill="none">
      <rect x="2" y="5" width="8" height="2" fill="currentColor" />
    </svg>
  );
};

/**
 * Hit/miss strip showing games_hit out of games_window
 * Green dots = hit, red dots = miss, displayed like the W/L strip at top of page
 */
const HitMissStrip = ({ gamesHit, gamesWindow, badge }) => {
  if (!gamesWindow || gamesWindow <= 0) return null;
  const misses = gamesWindow - gamesHit;
  const dots = [];

  // Show misses first (oldest), then hits (most recent)
  for (let i = 0; i < misses; i++) {
    dots.push(
      <div key={`m-${i}`} className="w-3 h-3 rounded-sm bg-danger/20 border border-danger/30 flex items-center justify-center">
        <span className="text-[7px] font-bold text-danger">X</span>
      </div>
    );
  }
  for (let i = 0; i < gamesHit; i++) {
    dots.push(
      <div key={`h-${i}`} className="w-3 h-3 rounded-sm bg-success/20 border border-success/30" />
    );
  }

  return (
    <div className="flex items-center gap-px">
      {dots}
    </div>
  );
};

/**
 * Threshold bar: shows season avg vs threshold line
 */
const ThresholdBar = ({ seasonAvg, threshold }) => {
  if (seasonAvg == null || threshold == null) return null;

  // Scale: threshold is the target, show how avg relates
  const maxVal = Math.max(seasonAvg, threshold) * 1.3;
  const avgPct = Math.min((seasonAvg / maxVal) * 100, 100);
  const threshPct = Math.min((threshold / maxVal) * 100, 100);
  const aboveThreshold = seasonAvg >= threshold;

  return (
    <div className="relative h-1.5 bg-border/20 rounded-full overflow-visible">
      {/* Season avg fill */}
      <div
        className={`absolute top-0 left-0 h-full rounded-full ${aboveThreshold ? 'bg-success/60' : 'bg-amber-400/60'}`}
        style={{ width: `${avgPct}%` }}
      />
      {/* Threshold marker */}
      <div
        className="absolute top-0 h-full w-px bg-text-secondary/60"
        style={{ left: `${threshPct}%` }}
      />
    </div>
  );
};

const lastName = (name) => {
  if (!name) return '';
  const parts = name.trim().split(' ');
  return parts[parts.length - 1];
};

/** Format a rate as a baseball average: .364 not 0.364 */
const fmtAvg = (v) => {
  if (v == null) return '—';
  if (v >= 1) return v.toFixed(3);
  return `.${Math.round(v * 1000).toString().padStart(3, '0')}`;
};

/**
 * Matchup vs today's opponent:
 * - batters: career line off the opposing starting pitcher
 * - starting pitchers: career line against the opposing lineup
 */
const MatchupRow = ({ matchup }) => {
  if (!matchup) return null;

  if (matchup.type === 'batter_vs_pitcher') {
    const pa = matchup.plate_appearances || 0;
    const opp = lastName(matchup.opponent_name) || 'starter';
    return (
      <div className="mt-2 pt-2 border-t border-border/20 flex items-center justify-between text-[10px]">
        <span className="text-text-secondary">
          vs <span className="font-semibold text-text-primary">{opp}</span>
        </span>
        {pa > 0 ? (
          <span className="font-mono text-text-primary">
            {matchup.hits}-{matchup.at_bats}
            <span className="text-text-secondary"> ({fmtAvg(matchup.batting_avg)})</span>
            {matchup.home_runs > 0 && <span className="text-orange-400 font-semibold"> · {matchup.home_runs} HR</span>}
            {matchup.strikeouts > 0 && <span className="text-text-secondary"> · {matchup.strikeouts} K</span>}
          </span>
        ) : (
          <span className="text-text-secondary italic">First meeting</span>
        )}
      </div>
    );
  }

  if (matchup.type === 'pitcher_vs_team') {
    const pa = matchup.plate_appearances || 0;
    return (
      <div className="mt-2 pt-2 border-t border-border/20 flex items-center justify-between text-[10px]">
        <span className="text-text-secondary">
          vs <span className="font-semibold text-text-primary">{matchup.opponent_abbr || 'lineup'}</span>
        </span>
        {pa > 0 ? (
          <span className="font-mono text-text-primary">
            {fmtAvg(matchup.batting_avg_against)}<span className="text-text-secondary"> BAA</span>
            {matchup.strikeouts > 0 && <span className="text-text-secondary"> · {matchup.strikeouts} K</span>}
            {matchup.home_runs > 0 && <span className="text-orange-400 font-semibold"> · {matchup.home_runs} HR</span>}
            <span className="text-text-secondary"> · {pa} BF</span>
          </span>
        ) : (
          <span className="text-text-secondary italic">No history</span>
        )}
      </div>
    );
  }

  return null;
};

const PlayerCard = ({ player }) => {
  const streak = player.top_streak || {};
  const badge = badgeConfig[streak.badge] || badgeConfig.warm;
  const hitRate = streak.hit_rate || 0;
  const extProb = player.extension_probability;

  return (
    <Card className="p-3 hover:bg-bg-card-hover/30 transition-colors">
      {/* Row 1: Player identity + hit rate */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {/* Team logo */}
          {player.team_logo && (
            <img src={player.team_logo} alt="" className="w-5 h-5 object-contain flex-shrink-0" />
          )}
          {/* Badge dot + name */}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${badge.color} ring-2 ${badge.ring}`} />
              <span className="text-sm font-bold text-text-primary truncate">
                {player.player_name}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] text-text-secondary">
              <span>{player.team_abbr}</span>
              <span className="opacity-50">|</span>
              <span>{player.position}</span>
            </div>
          </div>
        </div>

        {/* Hit rate - big number */}
        <div className="flex flex-col items-end flex-shrink-0">
          <span className={`font-mono text-lg font-bold leading-none ${
            hitRate >= 0.9 ? 'text-green-400' : hitRate >= 0.8 ? 'text-yellow-400' : 'text-text-primary'
          }`}>
            {streak.hit_rate_pct || `${Math.round(hitRate * 100)}%`}
          </span>
          <span className="text-[9px] text-text-secondary">hit rate</span>
        </div>
      </div>

      {/* Row 2: Streak description + threshold bar */}
      <div className="mb-2">
        <div className="flex items-center justify-between text-[10px] mb-0.5">
          <span className="font-semibold text-text-primary">
            {streak.description || `${streak.threshold}+ ${streak.stat_type}`}
          </span>
          {streak.season_avg != null && (
            <span className="font-mono text-text-secondary">
              Avg: {typeof streak.season_avg === 'number' ? streak.season_avg.toFixed(1) : streak.season_avg}
            </span>
          )}
        </div>
        <ThresholdBar seasonAvg={streak.season_avg} threshold={streak.threshold} />
      </div>

      {/* Row 3: Hit/miss strip */}
      <div className="mb-2">
        <HitMissStrip
          gamesHit={streak.games_hit}
          gamesWindow={streak.games_window}
          badge={streak.badge}
        />
      </div>

      {/* Row 4: Footer stats */}
      <div className="flex items-center justify-between text-[9px] text-text-secondary">
        <span>
          <span className="font-mono font-semibold text-text-primary">{streak.games_hit}</span>/{streak.games_window} games
        </span>

        {player.current_streak_length > 0 && (
          <span>
            Current run: <span className="font-mono font-semibold text-success">{player.current_streak_length}</span>
          </span>
        )}

        {/* Continuation signal */}
        <div className="flex items-center gap-1">
          {extProb != null && (
            <span className="font-mono">{Math.round(extProb * 100)}%</span>
          )}
          <SignalArrow signal={player.extension_signal} size="md" />
        </div>
      </div>

      {/* Row 5: Matchup vs today's opponent */}
      <MatchupRow matchup={player.matchup} />
    </Card>
  );
};

const HotStreaksStrip = ({ hotPlayers = [] }) => {
  if (!hotPlayers || hotPlayers.length === 0) return null;

  // Sort by hit rate desc, limit to 6
  const sorted = [...hotPlayers]
    .sort((a, b) => (b.top_streak?.hit_rate || 0) - (a.top_streak?.hit_rate || 0))
    .slice(0, 6);

  return (
    <div>
      <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2 px-1">
        Players to Watch
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {sorted.map((p, i) => (
          <PlayerCard key={p.player_id || i} player={p} />
        ))}
      </div>
    </div>
  );
};

export default HotStreaksStrip;
